import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useDashboardMetrics } from '@/hooks/useDashboardMetrics';
import { AlertCircle, CheckCircle, Clock } from 'lucide-react';

const DebugMetrics = () => {
  const {
    responseTimeMetrics,
    priorityDistribution,
    trendData,
    topCategories,
    topAnalysts,
    satisfactionMetrics,
    isLoading
  } = useDashboardMetrics();

  const getStatusIcon = (data: any, loading: boolean) => {
    if (loading) return <Clock className="h-4 w-4 text-yellow-500" />;
    if (data) return <CheckCircle className="h-4 w-4 text-green-500" />;
    return <AlertCircle className="h-4 w-4 text-red-500" />;
  };

  const getStatusText = (data: any, loading: boolean) => {
    if (loading) return 'Carregando...';
    if (data) return 'OK';
    return 'Erro';
  };

  const getStatusColor = (data: any, loading: boolean) => {
    if (loading) return 'bg-yellow-100 text-yellow-800';
    if (data) return 'bg-green-100 text-green-800';
    return 'bg-red-100 text-red-800';
  };

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-blue-600" />
          Debug - Status das Métricas
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {/* Response Time Metrics */}
          <div className="flex items-center justify-between p-3 border rounded-lg">
            <div>
              <p className="text-sm font-medium">Tempos de Resposta</p>
              <p className="text-xs text-gray-600">
                {responseTimeMetrics ? `${responseTimeMetrics.firstResponse}h` : 'N/A'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {getStatusIcon(responseTimeMetrics, isLoading)}
              <Badge className={getStatusColor(responseTimeMetrics, isLoading)}>
                {getStatusText(responseTimeMetrics, isLoading)}
              </Badge>
            </div>
          </div>

          {/* Priority Distribution */}
          <div className="flex items-center justify-between p-3 border rounded-lg">
            <div>
              <p className="text-sm font-medium">Distribuição Prioridade</p>
              <p className="text-xs text-gray-600">
                {priorityDistribution ? `${priorityDistribution.length} items` : 'N/A'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {getStatusIcon(priorityDistribution, isLoading)}
              <Badge className={getStatusColor(priorityDistribution, isLoading)}>
                {getStatusText(priorityDistribution, isLoading)}
              </Badge>
            </div>
          </div>

          {/* Trend Data */}
          <div className="flex items-center justify-between p-3 border rounded-lg">
            <div>
              <p className="text-sm font-medium">Dados de Tendência</p>
              <p className="text-xs text-gray-600">
                {trendData ? `${trendData.length} dias` : 'N/A'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {getStatusIcon(trendData, isLoading)}
              <Badge className={getStatusColor(trendData, isLoading)}>
                {getStatusText(trendData, isLoading)}
              </Badge>
            </div>
          </div>

          {/* Top Categories */}
          <div className="flex items-center justify-between p-3 border rounded-lg">
            <div>
              <p className="text-sm font-medium">Top Categorias</p>
              <p className="text-xs text-gray-600">
                {topCategories ? `${topCategories.length} categorias` : 'N/A'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {getStatusIcon(topCategories, isLoading)}
              <Badge className={getStatusColor(topCategories, isLoading)}>
                {getStatusText(topCategories, isLoading)}
              </Badge>
            </div>
          </div>

          {/* Top Analysts */}
          <div className="flex items-center justify-between p-3 border rounded-lg">
            <div>
              <p className="text-sm font-medium">Top Analistas</p>
              <p className="text-xs text-gray-600">
                {topAnalysts ? `${topAnalysts.length} analistas` : 'N/A'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {getStatusIcon(topAnalysts, isLoading)}
              <Badge className={getStatusColor(topAnalysts, isLoading)}>
                {getStatusText(topAnalysts, isLoading)}
              </Badge>
            </div>
          </div>

          {/* Satisfaction Metrics */}
          <div className="flex items-center justify-between p-3 border rounded-lg">
            <div>
              <p className="text-sm font-medium">Satisfação</p>
              <p className="text-xs text-gray-600">
                {satisfactionMetrics ? `NPS: ${satisfactionMetrics.npsScore}` : 'N/A'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {getStatusIcon(satisfactionMetrics, isLoading)}
              <Badge className={getStatusColor(satisfactionMetrics, isLoading)}>
                {getStatusText(satisfactionMetrics, isLoading)}
              </Badge>
            </div>
          </div>
        </div>

        {/* Debug Info */}
        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
          <p className="text-xs text-gray-600 mb-2">
            <strong>Status Geral:</strong> {isLoading ? 'Carregando dados...' : 'Dados carregados'}
          </p>
          <p className="text-xs text-gray-600">
            <strong>Última atualização:</strong> {new Date().toLocaleTimeString()}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default DebugMetrics;
